import React from 'react'
import clsx from 'clsx';
import { ErrorMessage, FormikProps } from 'formik'
import { Attributes, Label, LinkedTo, Validation } from 'app/modules/tp/builders/FormBuilder';
import TextInput from './TextInput';
import { traverseKeyObj, deepWriteKeyObj } from 'app/utils/keys';
import { Actions } from 'app/modules/tp/components/Modal/ModalEntity';

export type TextInputObj<T> = {
    type: "text",
    key: keyof T,
    isCurrency?: boolean,
    placeholder?: string,
    isRequired?: boolean,
    attributes?: Attributes
} & Attributes & Validation & Label & LinkedTo<T>


export type TextInputFieldProps<T> = {
    formik: FormikProps<T>,
    inputObj: TextInputObj<T>
    actions: Actions<T>,
    onChange?: (e: T) => void,
}

const CURRENCY_SYMBOL = '€ ';

const TextInputField = <T,>({ formik, inputObj, onChange }: TextInputFieldProps<T>) => {

    const fieldChange = (e: any) => {
        const v = e?.target?.value;
        if (!v) {
            return;
        }
        formik.setFieldValue(inputObj.key as string, v);
        onChange && onChange(formik.values);
    }

    const isRequiredField = (inputObj: TextInputObj<T>) => {
        return inputObj.validation?.spec?.presence === 'required'
    }

    const changeProps = onChange ? { onChange } : {}

    const typeProps = React.useMemo(() => {
        if (inputObj?.isCurrency) {
            return {
                prefix: CURRENCY_SYMBOL,
                style: { textAlign: 'right' },
            }
        }
        return {}
    }, [inputObj.isCurrency])

    React.useEffect(() => {
        if (inputObj.linkedTo && (formik.touched[inputObj.linkedTo] as boolean)) {
            const linkedValue = traverseKeyObj(formik.values, inputObj.linkedTo as string);
            if (linkedValue) {
                formik.setFieldValue(inputObj.key as string, linkedValue);
            }
        }
    }, [formik.values])

    return (<>
        {inputObj.label && (
            <label className={`fw-bold fs-6 mb-2 ${isRequiredField(inputObj) ? "required" : ""} `}>
                {inputObj.label}
            </label>
        )}

        <TextInput
            placeholder={`${inputObj.placeholder || ""}`}
            {...formik.getFieldProps(inputObj.key)}
            name={inputObj.key as string}
            className={clsx(
                'form-control form-control-solid mb-3 mb-lg-0',
                { 'is-invalid': formik?.touched[inputObj.key] && formik.errors[inputObj.key] },
                {
                    'is-valid': formik?.touched[inputObj.key] && !formik.errors[inputObj.key],
                }
            )}
            autoComplete='off'
            disabled={formik?.isSubmitting}
            {...inputObj.attributes}
            {...typeProps}
            {...changeProps}
        />
        {formik?.touched[inputObj.key] && formik.errors[inputObj.key] && (
            <ErrorMessage name={inputObj.key as string}
                render={(error: string) => (
                    <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                            <span role='alert'>{error}</span>
                        </div>
                    </div>
                )} />
        )}

    </>
    )
}
export default TextInputField;
